import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import {
  CancelBookingStatusInput,
  SupplierNotificationSettingEvent,
} from "@/gql/graphql";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { RichTextInput } from "@/lib/Components/Form/Inputs/RichTextInput";
import { BookingCancelledStatusInput } from "@/app/Bookings/Components/BookingCancelStatusInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { cancelBookingValidation } from "@/app/Bookings/Validation/bookingChangeStatusSchema";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { cancelBookingMutation } from "@/app/Bookings/GraphQL/cancelBookingMutation";
import { bookingRecordQuery } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";

type BookingCancelDialogProps = {
  bookingId: string;
};
export function BookingCancelDialog({ bookingId }: BookingCancelDialogProps) {
  const { mutateAsync } = useGqlMutation(cancelBookingMutation);
  const { close } = useDialogState();
  const { isAdmin } = useTenant();

  const { data } = useSuspenseGqlQuery(bookingRecordQuery, {
    id: bookingId,
  });

  const booking = data.booking;

  //The backend picks the before/after confirmation email off confirmed_at
  const cancellationEvent = booking.confirmed_at
    ? SupplierNotificationSettingEvent.BookingCancelledAfterConfirmation
    : SupplierNotificationSettingEvent.BookingCancelledBeforeConfirmation;

  const supplierAcceptsCancellationEmail = (
    booking.supplier.notificationSettings ?? []
  ).some((setting) => setting.event === cancellationEvent);

  return (
    <BaseDialog title="Cancel booking">
      <GenericForm<CancelBookingStatusInput>
        validate={toFormikValidate(cancelBookingValidation)}
        initialValues={{
          customer_note: booking.customer_note,
          release_relocation: booking.supplier.relist_on_cancellation,
          reason: "",
          status: null!,
          notify_supplier: supplierAcceptsCancellationEmail,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: booking.id,
            input: values,
          });

          await close();
        }}
      >
        <BookingCancelledStatusInput
          name={"status"}
          label={"Status"}
          initialOpen={true}
        />

        <SwitchInput
          name="release_relocation"
          label="Relist relocation"
          tooltip="Should the relocation be relisted?"
        />

        {isAdmin && supplierAcceptsCancellationEmail ? (
          <SwitchInput
            name="notify_supplier"
            label="Email supplier"
            tooltip="Send the cancellation email to the supplier. Turn off when the supplier told us about the cancellation."
          />
        ) : null}

        <TextAreaInput
          name={"reason"}
          label={"Internal note"}
          className="col-span-full"
        />

        <RichTextInput
          tooltip="This will appear on the customers booking pdf"
          name="customer_note"
          label="Customer note"
          className="col-span-full"
          optionalLabel
        />

        <DialogSubmitButton text="Cancel booking" />
      </GenericForm>
    </BaseDialog>
  );
}
